import type { App, Component } from 'vue';
import type { Store } from 'vuex';
import type { HTTPError } from 'ky';
import { createApp } from 'vue';
import PortalVue from 'portal-vue';
import { injectSvgSprite } from '@huntflow/ui';
import { VueQueryPlugin } from '@tanstack/vue-query';
import type { BootsrapOptionsType } from '@/shared/lib/bootstrap';
import type { I18NOptions } from '@/shared/legacy/lib/l10n';
import type { Route } from '@/shared/legacy/types/sentry';
import type { CreateRouter } from '@/app/legacy/entries/create-router';
import { createAsyncConnect } from '@/app/staged/async-connect';
import { bootstrap, waitBootstrap } from '@/shared/lib/bootstrap';
import { config } from '@/shared/legacy/lib/config/config';
import VueI18n from '@/shared/legacy/lib/l10n';
import { purifyHtml } from '@/shared/legacy/lib/purify-html';
import { transitionTrigger } from '@/shared/legacy/lib/transition-trigger';
import { emitter } from '@/shared/lib/emitter';
import { searchHighlightable } from '@/shared/lib/highlight';
import { queryClient } from '@/shared/lib/query-client';

declare module '@tanstack/vue-query' {
  // eslint-disable-next-line ts/consistent-type-definitions
  interface Register {
    // Use unknown so call sites must narrow explicitly.
    defaultError: HTTPError;
  }
}

declare module '@/shared/lib/emitter/global' {
  // eslint-disable-next-line ts/consistent-type-definitions
  interface EmitterEvents {
    'app:visibilitychange': boolean;
  }
}

type RouterOptions = {
  router: CreateRouter | Promise<CreateRouter>;
  route?: undefined;
} | {
  router?: undefined;
  route: Route;
};

type CreateOptions = {
  props?: Record<string, unknown>;
  store?: Store<any>;
  i18n?: I18NOptions;
  beforeMount?: (app: App) => Promise<void> | void;
  selector: string | Element;
  bootstrap?: Partial<BootsrapOptionsType>;
  asyncConnect?: boolean;
} & RouterOptions;

export const AppHelper = {
  async createApp<C extends Component>(
    component: C,
    {
      props,
      store,
      i18n,
      beforeMount,
      selector,
      bootstrap: bootstrapOptions,
      asyncConnect = true,
      router,
      route
    }: CreateOptions
  ) {
    emitter.emit('app:visibilitychange', !document.hidden);
    document.addEventListener('visibilitychange', () => {
      emitter.emit('app:visibilitychange', !document.hidden);
    });

    if (!bootstrapOptions?.skip) {
      await (bootstrapOptions?.wait ? waitBootstrap() : bootstrap());
    }
    if (store) {
      const vuexStoreConfig = store.getters['config/config'];
      if (vuexStoreConfig && Reflect.ownKeys(vuexStoreConfig).length === 0) {
        store.commit('config/setConfig', config.appConfig.config.value);
      }
    }
    const lang = i18n?.lang ?? config.appConfig.get('lang');
    const messages = i18n?.messages?.[lang] ?? window.Translations;

    const app = createApp(component, props);

    app.use(VueQueryPlugin, {
      queryClient
    });

    const routerOptions = router
      ? {
          router: await router,
          route
        }
      : {
          router,
          route
        };

    if (routerOptions.router) {
      app.use(routerOptions.router);
      await routerOptions.router.isReady();
    }
    if (store) {
      app.use(store);
    }
    app.directive('purify-html', purifyHtml);
    app.directive('transition-trigger', transitionTrigger);
    app.directive('search-highlightable', searchHighlightable);

    app.use(VueI18n, {
      messages: { [lang]: messages },
      lang
    });
    app.use(PortalVue, {
      portalName: false,
      portalTargetName: false
    });

    window.Sentry?.init(app, {
      ...routerOptions,
      account: config.appConfig.get('account'),
      organization: config.appConfig.get('accountNick')
    });

    await beforeMount?.(app);

    if (config.userPermissions.accountMemberId && asyncConnect && config.appConfig.get('notifierUrl')) {
      createAsyncConnect({
        accountMemberId: config.userPermissions.accountMemberId,
        pollerUrl: config.appConfig.get('notifierUrl')
      });
    }

    injectSvgSprite();

    return app.mount(selector);
  }
};
